/**
 * Arabic copy for the ops Reviews page «التقييمات والشكاوى» (tasks/post-checkout-review-and-replies.md Phases 2–3;
 * its complaints tab's words live in complaintLabels.js — tasks/complaints-to-fixes.md C19).
 *
 * Ops queue pages are Arabic-only (I18N_STRATEGY — only the crew's task screens are bilingual), so the
 * words live here, the way guestRequestLabels.js serves the requests hub. The API sends CODES; this
 * file is where they become words. Reason labels are NOT repeated here — they come from the locale
 * file the crew's own question uses (`tasks.guestAssessment.reasons`), so the two can never drift.
 */

// Airbnb's three guest-review categories — codes are Channex's.
export const SCORE_ORDER = ['cleanliness', 'communication', 'respect_house_rules'];
export const SCORE_LABELS = {
  cleanliness: 'النظافة',
  communication: 'التواصل',
  respect_house_rules: 'الالتزام بقواعد السكن'
};

export const REVIEW_STATUS = {
  needs_approval: 'بانتظار الاعتماد',
  sending: 'جارٍ الإرسال',
  sent: 'أُرسل',
  published: 'نُشر على Airbnb',
  failed: 'رُفض الإرسال',
  unknown: 'لم يتأكد الإرسال',
  expired: 'انتهت المهلة',
  already_reviewed: 'تم التقييم في Airbnb'
};

export const THANK_YOU_STATUS = {
  needs_approval: 'بانتظار الاعتماد',
  sending: 'جارٍ الإرسال',
  sent: 'أُرسلت',
  failed: 'رُفض الإرسال',
  unknown: 'لم يتأكد الإرسال',
  expired: 'انتهت المهلة'
};

// Our reply to a guest review (list 2) — `status` codes from be/models/GuestReviewReply.js.
export const REPLY_STATUS = {
  drafting: 'جارٍ تجهيز المسودة',
  needs_approval: 'بانتظار الاعتماد',
  sending: 'جارٍ الإرسال',
  sent: 'أُرسل — بانتظار النشر',
  published: 'نُشر',
  waiting_moderation: 'بانتظار مراجعة Booking.com',
  failed: 'رُفض الإرسال',
  unknown: 'لم يتأكد الإرسال',
  expired: 'انتهت المهلة',
  cannot_reply: 'لا يمكن الرد',
  skipped: 'تم التخطي',
  replied_elsewhere: 'تم الرد من مكان آخر'
};

// The checks a reply's text failed (be/services/guestReviews/replies/prepare.js `replyProblems`).
export const REPLY_PROBLEM_TEXT = {
  empty: 'لا يوجد نص للرد — اكتب الرد.',
  too_long: 'الرد أطول من المسموح — أربع جمل و١٠٠٠ حرف كحد أقصى.',
  language: 'الرد ليس بلغة تقييم الضيف.',
  money: 'الرد يذكر مالاً أو تعويضاً أو خصماً — غير مسموح في الرد العام.',
  contact_or_numbers: 'الرد يحتوي أرقاماً أو روابط أو وسيلة تواصل.',
  guest_name: 'الرد يذكر اسم الضيف — لا نذكر الأسماء في الرد العام.',
  private_note: 'الرد يكرر نص الملاحظة الخاصة — غير مسموح.'
};

// The private message to a guest about a private-note complaint (tasks/review-complaint-triage.md) —
// `followUp.status` codes from be/models/GuestReviewReply.js.
export const FOLLOWUP_STATUS = {
  drafting: 'جارٍ تجهيز المسودة',
  needs_approval: 'بانتظار الاعتماد',
  sending: 'جارٍ الإرسال',
  sent: 'أُرسلت',
  failed: 'رُفض الإرسال',
  unknown: 'لم يتأكد الإرسال',
  skipped: 'تم التخطي'
};
// The checks the message failed (be/services/guestReviews/replies/followUp.js `followUpProblems`).
export const FOLLOWUP_PROBLEM_TEXT = {
  empty: 'لا يوجد نص للرسالة — اكتبها.',
  too_long: 'الرسالة أطول من المسموح — ٧٠٠ حرف كحد أقصى.',
  money: 'الرسالة تذكر مالاً أو تعويضاً أو خصماً — غير مسموح.',
  contact_or_numbers: 'الرسالة تحتوي أرقاماً أو روابط أو وسيلة تواصل.',
  claims_fix: 'المسودة الآلية تقول إن المشكلة أُصلحت — عدّلها بنفسك لتؤكد ذلك إن كان صحيحاً.',
  promises: 'المسودة الآلية تَعِد بما سيحدث لاحقاً — عدّلها بنفسك إن كان الوعد صحيحاً.'
};

// Why ops skips a review (P4) — MUST MATCH be/services/guestReviews/replies/prepare.js SKIP_REASONS.
export const SKIP_REASONS = ['not_needed', 'handled_elsewhere', 'other'];
export const SKIP_REASON_LABELS = {
  not_needed: 'لا يحتاج رداً',
  handled_elsewhere: 'تم الرد بطريقة أخرى',
  other: 'أخرى'
};

// Why the last send did not land — `failure.code` from the server. Platform-neutral: the thank-you
// and replies go to Booking.com guests too.
export const FAILURE_TEXT = {
  refused: 'رفضت المنصة الإرسال. عدّل النص إن لزم ثم أعد الإرسال.',
  no_confirmation: 'لم يصل تأكيد من المنصة. تحقّق منها قبل إعادة الإرسال حتى لا يصل مرتين.',
  no_thread: 'لا توجد محادثة مع الضيف على المنصة.',
  already_replied: 'تقول المنصة إن للتقييم رداً بالفعل — تُحدَّث الحالة تلقائياً بعد التحقق، فلا تعِد الإرسال.',
  review_gone: 'لم يعد التقييم موجوداً على المنصة.'
};

export const CLEANER_ANSWER = { yes: 'نعم', no: 'لا' };

// The late verdict's codes — MUST MATCH be/services/guestReviews/lateVerdict.js `REASON`. The code is
// the contract (I18N_STRATEGY); the server's `displayText` is only the fallback for a code this file
// does not know yet — the utils/cleaningReasonCodes.js pattern.
const LATE_REASON_TEXT = {
  CLEAN_NOT_CLOSED: 'تأخر إغلاق التنظيف',
  CLEAN_OPEN_ON_TIME: 'التنظيف لم يُغلق بعد — ضمن الوقت',
  CHECKOUT_UNKNOWN: 'تعذّر تحديد موعد مغادرة الضيف'
};
export const lateReasonText = (verdict) => LATE_REASON_TEXT[verdict?.reasonCode] || verdict?.displayText || '';

export const TEXT = {
  // One page for reviews and complaints (tasks/complaints-to-fixes.md C19).
  pageTitle: 'التقييمات والشكاوى',
  pageSubtitle: 'تقييمنا لكل ضيف غادر ورسالة الشكر له والرد على تقييمات الضيوف — تُعتمد هنا قبل إرسالها — وشكاوى الضيوف حتى تُعالج ويُمنع تكرارها.',
  tabGuests: 'تقييم الضيوف المغادرين',
  tabReplies: 'الرد على التقييمات',
  listTitle: 'تقييم الضيوف المغادرين',
  refresh: 'تحديث',
  loading: 'جارٍ تحميل التقييمات…',
  loadError: 'تعذّر تحميل التقييمات. حاول مرة أخرى.',
  emptyTitle: 'لا توجد تقييمات بانتظار الإجراء',
  emptyHint: 'تظهر هنا إقامات الوحدات المفعّلة بعد إغلاق تنظيف المغادرة.',
  countNeedsApproval: 'بانتظار الاعتماد',
  countNeedsDecision: 'يحتاج قرار',
  countLate: 'تأخر إغلاق التنظيف',
  countUnverified: 'تعذّر تحديد موعد المغادرة',
  countNotPublished: 'أُرسل ولم يُنشر',
  reviewSendOff: 'إرسال تقييمات الضيوف متوقف حالياً — يمكن المراجعة والتعديل دون إرسال.',
  thankYouSendOff: 'إرسال رسائل الشكر متوقف حالياً.',
  replyDraftOff: 'كتابة المسودات الآلية متوقفة حالياً — اكتب الرد يدوياً.',
  lateTitle: 'بانتظار إغلاق التنظيف',
  lateHint: 'لا يمكن تجهيز التقييم قبل أن يغلق عامل النظافة مهمة المغادرة ويجيب عن سؤال الضيف.',
  waitingOn: 'بانتظار',
  unassigned: 'غير مكلّف',
  departed: 'غادر',
  since: 'منذ',
  cleanerSaid: 'رأي عامل النظافة',
  cleanerNote: 'ملاحظته',
  noAnswer: 'لم يُجب',
  openTask: 'مهمة التنظيف',
  taskLoadError: 'تعذّر تحميل مهمة التنظيف. حاول مرة أخرى.',
  reviewTitle: 'تقييمنا للضيف',
  thankYouTitle: 'رسالة الشكر',
  needsAttention: 'يحتاج انتباه — حدّد الدرجات والتوصية قبل الإرسال',
  recommended: 'يُنصح به',
  notRecommended: 'لا يُنصح به',
  recommendUnset: 'لم تُحدّد التوصية',
  approveReview: 'اعتماد وإرسال التقييم',
  approveThankYou: 'إرسال رسالة الشكر',
  edit: 'تعديل',
  save: 'حفظ',
  cancel: 'إلغاء',
  confirm: 'تأكيد',
  markAlreadyReviewed: 'قيّمه الفريق يدوياً في Airbnb',
  alreadyReviewedConfirm: 'هل قيّم الفريق هذا الضيف يدوياً في تطبيق Airbnb؟ بعد التأكيد لن يُرسل تقييمنا له.',
  resendChecked: 'تحقّقت من المنصة — أعد الإرسال',
  confirmDelivered: 'وصلت — تحقّقت من المنصة',
  slotNotReady: 'لم يصل سجل تقييم هذه الإقامة من Airbnb بعد — يُجلب تلقائياً ويصبح الإرسال متاحاً عند وصوله.',
  overrideReason: 'سبب مخالفة رأي عامل النظافة',
  overrideReasonPlaceholder: 'مثال: شكوى من الجيران أثناء الإقامة',
  reviewTextPlaceholder: 'نص التقييم العام',
  thankYouPlaceholder: 'نص رسالة الشكر',
  by: 'بواسطة',
  edited: 'معدّل',
  overridden: 'خالف رأي عامل النظافة',
  endsToday: 'ينتهي اليوم',
  deadlinePassed: 'انتهت المهلة',
  daysLeft: 'متبقٍ',
  day: 'يوم',
  twoDays: 'يومان',
  days: 'أيام',
  // ── List 2: replies ──
  repliesEmptyTitle: 'لا توجد تقييمات بانتظار الرد',
  repliesEmptyHint: 'تظهر هنا تقييمات الضيوف للوحدات المفعّلة، ويُجهَّز لكل منها رد لتعتمده.',
  received: 'وصل',
  openListing: 'فتح صفحة الوحدة على',
  score: 'التقييم',
  guestReview: 'تقييم الضيف',
  ourReply: 'ردنا',
  // The card's two halves and where each outgoing item goes (review-card layout, 4 Oct).
  guestSaid: 'ما قاله الضيف',
  weSend: 'ما سنرسله',
  publicReplyTitle: 'رد عام',
  publicReplyWhere: 'يظهر للجميع على',
  switchesOff: 'متوقف حالياً:',
  offReplySend: 'نشر الردود',
  offReplyDraft: 'المسودات الآلية للردود',
  offBookingReplies: 'ردود Booking.com (تُدار من لوحة Booking.com)',
  offFollowUpSend: 'إرسال الرسائل الخاصة',
  translation: 'الترجمة',
  privateNote: 'ملاحظة خاصة من الضيف',
  privateNoteBadge: 'للفريق الداخلي فقط',
  // The public-reply drafter never reads it; the complaint check and the private-message draft do
  // (tasks/review-complaint-triage.md) — the wording must not claim otherwise.
  privateNoteHint: 'لا تظهر في التقييم العام، ولا تدخل في مسودة الرد العام.',
  attention: 'يحتاج انتباه',
  attentionLowScore: 'تقييم منخفض',
  attentionPrivateNote: 'فيه ملاحظة خاصة لم تُفحص',
  attentionUrgent: 'عاجل',
  complaintOpen: 'شكوى مفتوحة — عرض',
  complaintClosed: 'شكوى مغلقة — عرض',
  complaintPublic: 'شكوى في التقييم العام',
  complaintPrivate: 'شكوى في الملاحظة الخاصة',
  complaintLabels: 'اختيار الضيف في Airbnb',   // "what to improve" labels — named in brackets (tasks/airbnb-review-labels.md L8)
  noComplaint: 'لا توجد شكوى في التقييم',
  triagePending: 'جارٍ فحص التقييم…',
  // ── The private message (tasks/review-complaint-triage.md) ──
  followUpTitle: 'رسالة خاصة',
  followUpHint: 'للضيف فقط، في محادثة الحجز على المنصة',
  followUpPlaceholder: 'نص الرسالة الخاصة للضيف',
  followUpNoDraft: 'لم تُجهَّز مسودة — اكتب الرسالة.',
  writeFollowUp: 'اكتب الرسالة',
  followUpAddYourLine: 'مسودة آلية — أضف عبر «تعديل» ما يناسب: تم الإصلاح، أخذنا الملاحظة، أو غيرها.',
  followUpProblemsTitle: 'لا يمكن إرسال المسودة الآلية كما هي:',
  sendFollowUp: 'إرسال للضيف',
  skipFollowUpConfirm: 'لن تُرسل رسالة خاصة لهذا الضيف. متأكد؟',
  confirmFollowUpSent: 'تحقّقت — الرسالة وصلت في المحادثة',
  draftPending: 'جارٍ تجهيز المسودة…',
  noDraft: 'لم تُجهَّز مسودة لهذا التقييم — اكتب الرد.',
  replyPlaceholder: 'نص الرد العام بلغة الضيف',
  approveReply: 'نشر الرد العام',
  writeReply: 'اكتب الرد',
  problemsTitle: 'لا يمكن الإرسال حتى يُعدَّل الرد:',
  skip: 'تخطٍّ',
  skipConfirm: 'اختر سبب التخطي — لن يُرسل رد لهذا التقييم بعدها.',
  skipNotePlaceholder: 'ملاحظة (اختياري)',
  confirmPublished: 'تحقّقت — الرد ظاهر على المنصة',
  windowClosing: 'تنتهي مهلة الرد خلال أقل من ساعة — لم يعد الإرسال ممكناً.',
  platformOff: 'الرد على تقييمات هذه المنصة غير مفعّل بعد.',
  notPublished: 'أُرسل ولم يظهر على المنصة بعد — تحقّق منها ولا تعِد الإرسال.',
  unexpected: 'حدث خطأ غير متوقع. حاول مرة أخرى.'
};

// Server refusal codes (be/utils/errors.js GUEST_REVIEW_xxx) → words. Never read meaning off the
// server's Arabic `error` text (I18N_STRATEGY).
const ERROR_TEXT = {
  GUEST_REVIEW_001: 'لم يعد هذا العنصر موجوداً.',
  GUEST_REVIEW_002: 'تغيّر هذا العنصر أو أُرسل — تم تحديث القائمة، راجعه ثم أعد المحاولة.',
  GUEST_REVIEW_003: 'الإرسال متوقف حالياً لهذا النوع.',
  GUEST_REVIEW_004: 'لم يصل سجل تقييم هذه الإقامة من Airbnb بعد.',
  GUEST_REVIEW_006: 'اكتب سبب مخالفة رأي عامل النظافة.',
  GUEST_REVIEW_007: 'انتهت المهلة — لم يعد الإرسال ممكناً.',
  GUEST_REVIEW_009: 'عدّل الضيف تقييمه — تُجهَّز مسودة جديدة.'
};
const INVALID_FIELD_TEXT = {
  scores: 'اختر درجة من ١ إلى ٥ لكل بند.',
  recommended: 'حدّد إن كان يُنصح بالضيف.',
  publicText: 'اكتب نص التقييم.',
  text: 'اكتب نص الرسالة.',
  overrideReason: 'سبب المخالفة طويل — اختصره.',
  reason: 'اختر سبب التخطي.',
  note: 'الملاحظة طويلة — اختصرها.'
};
// GUEST_REVIEW_008 — something changed after the item opened; `details.reason` says what.
const NOT_SENDABLE_TEXT = {
  unit_off: 'هذه الوحدة لم تعد مفعّلة لتقييم الضيوف — تُدار يدوياً في تطبيق المنصة.',
  cancelled: 'الحجز ملغى — لن يُرسل شيء.',
  not_departed: 'الضيف لم يغادر بعد — يُرسل بعد موعد المغادرة أو بعد تسجيل خروجه.',
  no_thread: 'لا توجد محادثة مع الضيف على المنصة — تواصل معه من تطبيق المنصة.'
};

/** A refused action, in words — from the code when there is one. */
export const errorText = (err) => {
  const data = err?.response?.data;
  if (data?.code === 'GUEST_REVIEW_005') {
    const problems = data?.details?.problems;
    // Replies and the private message share the code; their checks have their own wording.
    if (Array.isArray(problems) && problems.length) return problems.map(p => REPLY_PROBLEM_TEXT[p] || FOLLOWUP_PROBLEM_TEXT[p] || p).join(' ');
    return INVALID_FIELD_TEXT[data?.details?.field] || INVALID_FIELD_TEXT.scores;
  }
  if (data?.code === 'GUEST_REVIEW_008') return NOT_SENDABLE_TEXT[data?.details?.reason] || ERROR_TEXT.GUEST_REVIEW_002;
  if (data?.code && ERROR_TEXT[data.code]) return ERROR_TEXT[data.code];
  return data?.error || TEXT.unexpected;
};
